import { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { AppBar } from './components/AppBar';
import { CameraView } from './components/CameraView';
import { PaywallOverlay } from './components/PaywallOverlay';
import { ReportCard } from './components/ReportCard';
import { ShareGuideModal } from './components/ShareGuideModal';
import { Toast } from './components/Toast';
import { UsageStrip } from './components/UsageStrip';
import { DAILY_QUOTA, MOCK_RESULT } from './constants';
import { useCamera } from './hooks/useCamera';
import { useLocalUid } from './hooks/useLocalUid';
import { useToast } from './hooks/useToast';
import { detectDog } from './services/level1';
import { getPoseLandmarks } from './services/level2';
import { ensureModelsLoaded, runAnalysis } from './services/runAnalysis';
import type { PoseLandmarksFrame } from './services/level2';
import type { AnalysisResult } from './types';

const POSE_HISTORY_MAX = 20;
/** 有狗时：密集姿态采样，供情绪多帧计算 */
const POSE_INTERVAL_MS = 150;
/** 无狗时：仅低频检测是否进框，保证画面流畅 */
const SEARCH_INTERVAL_MS = 700;
/** 超过此时长未检测到狗则视为「狗离开」，降回低频检测 */
const DOG_LOST_THRESHOLD_MS = 2800;
/** 实时分析间隔：仅在有狗时跑，结果浮在画面旁 */
const LIVE_ANALYSIS_INTERVAL_MS = 2000;
/** 三个结果浮窗保持显示时长，超时或换新狗时刷新 */
const LIVE_RESULT_DISPLAY_MS = 30000;

type AppState = 'shoot' | 'result';

function getShareText(result: AnalysisResult, petName: string | null): string {
  const prefix = petName ? `${petName}的小报告 🐾\n` : '用 PawAI 读懂了毛孩子 🐾\n';
  const parts = [
    `谁家的小可爱：${result.breed} ${result.breedMeta}`,
    `TA 的小情绪：${result.emotion} ${result.emotionMeta}`,
    `体型如何：${result.health} ${result.healthMeta}`,
  ];
  return prefix + parts.join('\n');
}

export default function App() {
  const camera = useCamera();
  useLocalUid(); // 匿名 ID 持久化，供后续服务端关联
  const [toastMessage, showToast] = useToast();
  const [appState, setAppState] = useState<AppState>('shoot');
  const [dailyUsed, setDailyUsed] = useState(0);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  /** 实时分析结果：打开相机后持续后台分析，有狗时展示在画面旁；拍一下 = 定格此结果用于分享 */
  const [liveResult, setLiveResult] = useState<AnalysisResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [shareGuideOpen, setShareGuideOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const reportCardRef = useRef<HTMLDivElement>(null);
  const poseHistoryRef = useRef<PoseLandmarksFrame[]>([]);
  const liveAnalysisTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveAnalysisRunningRef = useRef(false);
  /** 三个结果显示 30 秒后自动隐藏；每次有新结果时重置 */
  const liveResultExpireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 最近是否检测到狗：有狗时密集算姿态+分析，无狗时仅低频检测，保证画面流畅 */
  const [hasDogRecently, setHasDogRecently] = useState(false);
  const lastDogSeenAtRef = useRef(0);
  /** 首次加载：模型与相机都就绪后再显示主界面，避免用户以为已就绪 */
  const [modelsReady, setModelsReady] = useState(false);

  const remaining = Math.max(0, DAILY_QUOTA - dailyUsed);
  const appReady = modelsReady && camera.active;

  // 进入应用即开始加载模型，不等到打开相机
  useEffect(() => {
    ensureModelsLoaded()
      .then(() => setModelsReady(true))
      .catch(() => setModelsReady(true)); // 失败也放行，避免卡在加载
  }, []);
  // 相机就绪后若模型未就绪可再触发一次（兜底）
  useEffect(() => {
    if (!camera.active) return;
    ensureModelsLoaded().then(() => setModelsReady(true)).catch(() => {});
  }, [camera.active]);

  // 拍摄态：无狗时仅低频检测（流畅），有狗时密集姿态+分析
  useEffect(() => {
    if (appState !== 'shoot' || !camera.active) return;
    const video = camera.videoRef.current;
    if (!video) return;
    let cancelled = false;
    const intervalMs = hasDogRecently ? POSE_INTERVAL_MS : SEARCH_INTERVAL_MS;
    const tick = () => {
      if (cancelled || video.readyState < 2) return;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0);
      const l1 = detectDog(canvas);
      if (l1?.hasDog) {
        lastDogSeenAtRef.current = Date.now();
        if (!hasDogRecently) setHasDogRecently(true);
        if (hasDogRecently) {
          const frame = getPoseLandmarks(canvas);
          if (frame) {
            const prev = poseHistoryRef.current;
            const next = [...prev, frame].slice(-POSE_HISTORY_MAX);
            poseHistoryRef.current = next;
          }
        }
      } else if (hasDogRecently && Date.now() - lastDogSeenAtRef.current > DOG_LOST_THRESHOLD_MS) {
        setHasDogRecently(false);
      }
    };
    const id = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [appState, camera.active, camera.videoRef, hasDogRecently]);

  // 仅在有狗时周期执行实时分析；无狗时保留上次结果不清空，狗回来继续更新
  useEffect(() => {
    if (appState !== 'shoot' || !camera.active) return;
    if (!hasDogRecently) return;
    const video = camera.videoRef.current;
    if (!video) return;
    let cancelled = false;
    const run = async () => {
      if (cancelled || liveAnalysisRunningRef.current) return;
      liveAnalysisRunningRef.current = true;
      try {
        await ensureModelsLoaded();
        if (cancelled) return;
        const history = [...poseHistoryRef.current];
        const out = await runAnalysis(video, history.length > 0 ? history : null);
        if (cancelled) return;
        if (out.result && typeof out.result.breed === 'string') {
          setLiveResult({ ...out.result });
          if (liveResultExpireTimerRef.current) clearTimeout(liveResultExpireTimerRef.current);
          liveResultExpireTimerRef.current = setTimeout(() => {
            setLiveResult(null);
            liveResultExpireTimerRef.current = null;
          }, LIVE_RESULT_DISPLAY_MS);
        }
        // 无结果时不主动清空，由 30 秒定时器或换新狗后的新结果刷新
      } catch {
        if (!cancelled) { /* 保留上次 liveResult，不清空 */ }
      } finally {
        liveAnalysisRunningRef.current = false;
      }
    };
    run();
    const id = setInterval(run, LIVE_ANALYSIS_INTERVAL_MS);
    liveAnalysisTimerRef.current = id;
    return () => {
      cancelled = true;
      if (liveAnalysisTimerRef.current) {
        clearInterval(liveAnalysisTimerRef.current);
        liveAnalysisTimerRef.current = null;
      }
      if (liveResultExpireTimerRef.current) {
        clearTimeout(liveResultExpireTimerRef.current);
        liveResultExpireTimerRef.current = null;
      }
    };
  }, [appState, camera.active, camera.videoRef, hasDogRecently]);

  const result: AnalysisResult = analysisResult ?? MOCK_RESULT;

  const captureSnapshot = useCallback((): string | null => {
    const video = camera.videoRef.current;
    if (!video || video.readyState < 2) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9);
  }, [camera.videoRef]);

  const handleShoot = useCallback(async () => {
    if (remaining <= 0) {
      showToast('今天的次数用完啦，升级后继续玩～');
      return;
    }
    const video = camera.videoRef.current;
    if (!video || video.readyState < 2) {
      showToast('相机未就绪，请稍候再拍');
      return;
    }
    const url = captureSnapshot();
    if (!url) {
      showToast('无法截取画面，请重试');
      return;
    }
    setSnapshotUrl(url);
    // 有实时结果时直接定格当前结果用于分享，不再跑一次分析
    const safeResult = liveResult && typeof liveResult.breed === 'string' && typeof liveResult.emotion === 'string' && typeof liveResult.health === 'string'
      ? { ...liveResult }
      : null;
    if (safeResult) {
      setAnalysisResult(safeResult);
      setDailyUsed((d) => d + 1);
      setAppState('result');
      return;
    }
    setAnalyzing(true);
    try {
      await ensureModelsLoaded();
      const history = [...poseHistoryRef.current];
      const out = await runAnalysis(video, history.length > 0 ? history : null);
      if (out.result && typeof out.result.breed === 'string') {
        setAnalysisResult({ ...out.result });
        setDailyUsed((d) => d + 1);
        setAppState('result');
      } else {
        if ('noDogHint' in out && out.noDogHint === 'person') {
          showToast('当前画面里是两脚兽哦，请对准狗狗～');
        } else {
          showToast('没检测到狗狗哦，把 TA 放进画面再试一次');
        }
      }
    } catch (e) {
      console.error(e);
      showToast('分析失败，请重试');
    } finally {
      setAnalyzing(false);
    }
  }, [remaining, liveResult, captureSnapshot, showToast, camera.videoRef]);

  const handleAgain = useCallback(() => {
    setSnapshotUrl(null);
    setAnalysisResult(null);
    setLiveResult(null);
    setHasDogRecently(false);
    poseHistoryRef.current = [];
    lastDogSeenAtRef.current = 0;
    if (liveResultExpireTimerRef.current) {
      clearTimeout(liveResultExpireTimerRef.current);
      liveResultExpireTimerRef.current = null;
    }
    setAppState('shoot');
  }, []);

  const handleSave = useCallback(async () => {
    const el = reportCardRef.current;
    if (!el) {
      showToast('无法保存');
      return;
    }
    setSaving(true);
    try {
      const canvas = await html2canvas(el, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `PawAI-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      showToast('已保存');
    } catch {
      showToast('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  }, [showToast]);

  const handleShareClick = useCallback(() => {
    setShareGuideOpen(true);
  }, []);

  const handleSaveAndShare = useCallback(
    (petName: string, _petAge: string | null) => {
      const text = getShareText(result, petName);
      if (navigator.share) {
        navigator
          .share({
            title: petName ? `${petName} · PawAI` : 'PawAI · 读懂毛孩子',
            text,
          })
          .then(() => showToast('已打开分享'))
          .catch((e) => {
            if (e.name !== 'AbortError') {
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(text).then(() => showToast('已复制，去微信/微博分享给朋友吧'));
              } else {
                showToast('请使用手机浏览器或支持分享的 App 打开');
              }
            }
          });
      } else {
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(text).then(() => showToast('已复制，去微信/微博分享给朋友吧'));
        } else {
          showToast('请使用手机浏览器或支持分享的 App 打开');
        }
      }
    },
    [result, showToast]
  );

  const handleWhistle = useCallback(() => {
    showToast('叫 TA 看镜头～ 实机会抓最佳瞬间');
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 5200;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch {
      // ignore
    }
  }, [showToast]);

  return (
    <div className={`app ${appState === 'result' ? 'state-result' : ''}`}>
      {!appReady && (
        <div className="app-loading-overlay" role="status" aria-live="polite">
          <div className="app-loading-content">
            <span className="app-loading-icon" aria-hidden>🐾</span>
            <p className="app-loading-title">正在加载模型与相机</p>
            <p className="app-loading-hint">请稍候，马上就好</p>
          </div>
        </div>
      )}
      <AppBar onWhistle={handleWhistle} onUpgrade={() => setPaywallOpen(true)} />
      <UsageStrip remaining={remaining} />

      <main className="main">
        {/* 拍摄态：打开即持续检测，有狗时实时结果浮在画面旁；拍一下 = 定格当前结果并分享 */}
        <div className="screen screen-shoot">
          <div className="camera-wrap">
            {analyzing && snapshotUrl ? (
              <div className="camera-area camera-captured" aria-busy="true">
                <div className="capture-flash" key={snapshotUrl} aria-hidden />
                <img src={snapshotUrl} alt="刚拍下的画面" className="capture-preview-img" />
                <div className="analyzing-overlay">
                  <span className="analyzing-title">分析中…</span>
                  <span className="analyzing-steps">识别品种 · 分析情绪 · 评估体态</span>
                </div>
              </div>
            ) : (
              <CameraView camera={camera} />
            )}
            {liveResult && !analyzing && (
              <div className="live-result-overlay" role="status" aria-live="polite">
                <div className="live-result-item live-result-breed">
                  <span className="live-result-dot" aria-hidden />
                  <span className="live-result-label">谁家的小可爱</span>
                  <span className="live-result-value">{liveResult.breed} {liveResult.breedMeta}</span>
                </div>
                <div className="live-result-item live-result-emotion">
                  <span className="live-result-dot" aria-hidden />
                  <span className="live-result-label">TA 的小情绪</span>
                  <span className="live-result-value">{liveResult.emotion} {liveResult.emotionMeta}</span>
                </div>
                <div className="live-result-item live-result-health">
                  <span className="live-result-dot" aria-hidden />
                  <span className="live-result-label">体型如何</span>
                  <span className="live-result-value">{liveResult.health} {liveResult.healthMeta}</span>
                </div>
              </div>
            )}
          </div>
          <div className="shoot-cta">
            <p className="shoot-hint">
              {analyzing ? '正在读懂 TA' : liveResult ? '拍下这一刻并分享' : '对准 TA，拍一下'}
            </p>
            <button
              type="button"
              className="btn-shoot"
              onClick={handleShoot}
              disabled={remaining <= 0 || analyzing || !liveResult}
            >
              {analyzing ? '分析中…' : liveResult ? '分享小可爱' : '分享小可爱'}
            </button>
          </div>
          <div className="status-bar" role="status" aria-live="polite">
            {camera.error ? (
              <span className="error">{camera.error}</span>
            ) : analyzing ? (
              '正在识别 TA～'
            ) : liveResult ? (
              '已识别，点击分享小可爱保存或分享'
            ) : (
              '还没看到狗狗哦，把镜头对准 TA 吧～'
            )}
          </div>
        </div>

        {/* 结果态 */}
        <div className="screen screen-result">
          <ReportCard
            ref={reportCardRef}
            result={result}
            snapshotUrl={snapshotUrl}
            onSave={handleSave}
            onShare={handleShareClick}
            onAgain={handleAgain}
            saveDisabled={saving}
          />
        </div>

        <button
          type="button"
          className="btn-pro"
          onClick={() => (remaining <= 0 ? showToast('今天的次数用完啦，升级后继续玩～') : setPaywallOpen(true))}
          aria-label="生成专业报告（付费功能预留）"
        >
          <span className="pro-badge">Pro</span>
          <span>生成专业报告 · 更多模版</span>
        </button>
      </main>

      <PaywallOverlay open={paywallOpen} onClose={() => setPaywallOpen(false)} />
      <ShareGuideModal
        open={shareGuideOpen}
        onClose={() => setShareGuideOpen(false)}
        onSaveAndShare={handleSaveAndShare}
      />
      <Toast message={toastMessage} />
    </div>
  );
}
